import React, { useCallback } from 'react';
import { Button, ButtonGroup, Flex, Slider, Switch, View, } from '@adobe/react-spectrum';
import { EditorActions } from 'app/stores/editor/actions';
import { HistoryActions } from 'app/stores/history/actions';
import { ProjectActions } from 'app/stores/project/actions';
import { getCurrentPage, getCurrentSlideSize } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { SlideView } from 'app/views/components/editor/project/SlideView';
import { Text } from 'app/views/components/typography/Text';
import { useConnect } from 'app/views/hooks/useConnect';
import { Preview } from '../../Preview';
import { ColorsGrid } from './Colors';
export const ProjectBackground = () => {
    const dispatch = useAppDispatch();
    const { padding, corner, shadow, background, defaultColor } = useConnect(state => {
        var _a, _b, _c, _d, _e, _f, _g;
        const slide = getCurrentPage(state);
        const { colors } = state.editor;
        return {
            padding: (_b = (_a = slide.style) === null || _a === void 0 ? void 0 : _a.padding) !== null && _b !== void 0 ? _b : 0,
            corner: (_d = (_c = slide.style) === null || _c === void 0 ? void 0 : _c.corner) !== null && _d !== void 0 ? _d : 0,
            shadow: (_f = (_e = slide.style) === null || _e === void 0 ? void 0 : _e.shadow) !== null && _f !== void 0 ? _f : 0,
            background: (_g = slide.style) === null || _g === void 0 ? void 0 : _g.background,
            defaultColor: colors[0],
        };
    });
    const onPaddingChange = useCallback((value) => {
        dispatch(ProjectActions.setPadding({ padding: value }));
    }, [dispatch]);
    const onCornerRadiusChange = useCallback((value) => {
        dispatch(ProjectActions.setCornerRadius({ radius: value }));
    }, [dispatch]);
    const onShadowChange = useCallback((value) => {
        dispatch(ProjectActions.setShadow({ shadow: value }));
    }, [dispatch]);
    const onSolidBackgroundSelect = useCallback((value) => {
        dispatch(ProjectActions.setBackground({
            background: value
                ? {
                    type: 'solid',
                    color: value,
                }
                : undefined,
        }));
    }, [dispatch]);
    const onBackgroundSwitch = useCallback(() => {
        if (background) {
            onSolidBackgroundSelect(undefined);
        }
        else {
            onSolidBackgroundSelect(defaultColor);
        }
    }, [defaultColor, background, onSolidBackgroundSelect, dispatch]);
    const onCancel = React.useCallback(() => {
        dispatch(HistoryActions.reject());
        dispatch(EditorActions.resetAppController());
    }, [dispatch]);
    const onApply = React.useCallback(() => {
        dispatch(HistoryActions.commit());
        dispatch(EditorActions.resetAppController());
    }, [dispatch]);
    return (React.createElement(View, { flex: "1 1 auto", overflow: "hidden", UNSAFE_className: "background-view" },
        React.createElement(Flex, { width: "100%", height: "100%", direction: "row", flex: "1" },
            React.createElement(View, { flex: "1", padding: "size-200" },
                React.createElement(Preview, { className: "background-view", itemSelector: getCurrentSlideSize, Content: SlideView })),
            React.createElement(View, { backgroundColor: "gray-50", minWidth: "size-3400", maxWidth: "size-3400", width: "size-3400", padding: "size-400", borderStartWidth: "thin", borderStartColor: "gray-200" },
                React.createElement(Flex, { direction: "column", gap: "size-400" },
                    React.createElement(View, { flex: "1", paddingBottom: "size-200" },
                        React.createElement(Flex, { direction: "column" },
                            React.createElement(Slider, { label: "Padding", minValue: 0, maxValue: 100, value: padding, width: "100%", onChange: onPaddingChange }),
                            React.createElement(Slider, { label: "Rounded", minValue: 0, maxValue: 50, value: corner, width: "100%", onChange: onCornerRadiusChange }),
                            React.createElement(Slider, { label: "Shadow", minValue: 0, maxValue: 50, value: shadow, width: "100%", onChange: onShadowChange }),
                            React.createElement(Flex, { direction: "column", gap: "size-50" },
                                React.createElement(Flex, { direction: "row", alignItems: "center" },
                                    React.createElement(View, { flex: "1" },
                                        React.createElement(Text, { size: "XS" }, "Background fill")),
                                    React.createElement(Switch, { isSelected: !!background, onChange: onBackgroundSwitch })),
                                React.createElement(ColorsGrid, { onSelect: onSolidBackgroundSelect })))),
                    React.createElement(Flex, { justifyContent: "center", alignItems: "center" },
                        React.createElement(ButtonGroup, null,
                            React.createElement(Button, { variant: "secondary", onPress: onCancel }, "Cancel"),
                            React.createElement(Button, { variant: "cta", onPress: onApply }, "Apply"))))))));
};
